import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Ticket } from 'src/app/shared/models';
import { LocaleService, NotificationService, StorageService } from 'src/app/shared/services';
import { terminalKeyFromUrl } from 'src/app/shared/utils/queue-route';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {

  @Input() bgColor: string;
  @Input() logoUrl: string;
  @Input() logoHeight = '40px';
  @Input() showText = false;
  @Input() textColor = 'white';
  @Output() logoClick = new EventEmitter();
  @Output() digitalQueueClick = new EventEmitter();

  langs = [
    {
      code: 'pt-BR',
      name: 'Brasil'
    },
    {
      code: 'pt-PT',
      name: 'Portugal'
    },
    {
      code: 'en-US',
      name: 'EUA'
    }
  ];

  hasTicketChanged: boolean = false;
  tickets: Ticket[];
  eventNames = ['TicketCheckin', 'PrevisionChanged', 'TicketCalled', 'TicketStart', 'TicketComplete', 'TicketCancel'];
  subscription: Subscription;

  constructor(
    private translateService: TranslateService,
    private localeService: LocaleService,
    private notificationService: NotificationService,
    private router: Router,
    private storageService: StorageService
  ) { }

  ngOnInit() {
    this.tickets = this.contextTickets();
    this.addTicketsToChannels();
    this.subscribe();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  subscribe(): void {
    this.subscription = this.notificationService.notifications.subscribe((response) => {
      const currentTicket = this.contextTickets()[0];
      if (currentTicket && response.ticket.id !== currentTicket.id &&
          this.contextTickets().some(ticket => ticket.id === response.ticket.id)) {
        if (this.eventNames.includes(response.eventName)) {
          this.changeTicketStatus();
        }
      }
    });
  }

  addTicketsToChannels(): void {
    const currentTicket = this.contextTickets()[0];
    if (!currentTicket) return;

    this.tickets.forEach(ticket => {
      if (ticket && ticket.id !== currentTicket.id) {
        this.notificationService.add(ticket.id, ticket.serviceConfigId, this.eventNames)
      }
    });
  }

  next(): void {
    const terminalKey = terminalKeyFromUrl(this.router.url);
    this.router.navigate(terminalKey ? ['/queue', terminalKey, 'tickets'] : ['/tickets']);
    this.hasTicketChanged = false;
  }

  goHome(): void {
    this.router.navigate(['/home']);
  }

  changeTicketStatus(): void {
    // Idempotente de propósito: a mesma notificação pode chegar duas vezes
    // (fast path + reconciliação via GET). Com toggle, o 2º send anulava o 1º
    // e o badge nunca aparecia. O reset continua no `next()`.
    this.hasTicketChanged = true;
  }

  shouldHideBadge(): boolean {
    return this.isRouteTicketList() || this.isRouteHome() || !this.hasTicketChanged;
  }

  isRouteTicketList(): boolean {
    return this.router.url === '/tickets' || /^\/queue\/[^/]+\/tickets(?:[?#]|$)/.test(this.router.url);
  }

  private contextTickets(): Ticket[] {
    const terminalKey = terminalKeyFromUrl(this.router.url);
    return terminalKey ? this.storageService.getTicketsForTerminal(terminalKey) : this.storageService.tickets;
  }

  isRouteHome(): boolean {
    return this.router.url === '/home';
  }

  setLang(lang: string) {
    this.translateService.use(lang)
      .subscribe(() => this.localeService.storeUserLocale(lang));
  }

  get currentLang() {
    return this.localeService.getUserLocale();
  }

  onLogoClick() {
    this.logoClick.emit();
  }

  onDigitalQueueClick() {
    this.digitalQueueClick.emit();
  }

}
